/**
 * The Inbox — Metanoia's capture-and-decide pipeline.
 *
 * Design principle: a captured thought is NOT a task. Forcing every capture into a
 * Task is what produces an intimidating list of obligations out of what was really a
 * concern, an idea, or a note to keep. "I should improve my relationship with my
 * brother" might become a quest, a habit, a reflection prompt — or nothing at all.
 * So an InboxItem carries its own kind, and only becomes an action if the user
 * decides it should.
 *
 * Pipeline: CAPTURE → CLARIFY → CATEGORIZE → PRIORITIZE → ROUTE → SCHEDULE/STORE
 */

// The habit schedule shape is reused verbatim for a recurring destination's
// frequency — inventing a parallel one here would guarantee the two drift.
import type { HabitSchedule } from './index';
import { scheduleMissingDays } from '../utils/habitSchedule';

/**
 * What a captured item actually IS (decided in Clarify).
 *
 * ⚠️ KIND is deliberately ORTHOGONAL to destination. Earlier versions also offered
 * "a project / a habit / a skill / a possible goal", which are Metanoia ACTION TYPES —
 * i.e. answers to "what should it become?", not "what is it?". That made Clarify and
 * Route ask the same question twice. Kinds describe the thought; destinations decide
 * its fate. "Meditate every day" is an *action* whose destination is a Habit.
 */
export type InboxKind =
  | 'unclear'     // not yet clarified — the default at capture
  | 'action'      // a concrete thing to do
  | 'concern'     // a worry that needs addressing, not necessarily a task
  | 'idea'        // worth keeping, no obligation
  | 'decision'    // a choice to be made
  | 'commitment'  // something promised to someone
  | 'reminder'    // time-bound nudge
  | 'reference'   // information to keep, never actioned
  | 'delegate'    // belongs to someone else
  | 'none';       // requires nothing

/**
 * The FUNNEL. Each stage brings a captured thought one decision closer to becoming
 * something real; `route` is the exit. An item sits at exactly one stage.
 *
 * Kept separate from `status` on purpose: "where in the funnel" and "still in the
 * funnel at all" are different questions, and conflating them is what made the first
 * version incoherent (a focus-set *flag* was masquerading as a stage).
 */
export type InboxStage = 'capture' | 'clarify' | 'prioritize' | 'place' | 'classify' | 'route';

/** Whether the item is still moving through the funnel, or has left it. */
export type InboxStatus = 'open' | 'parked' | 'archived' | 'routed';

/**
 * ⚠️⚠️ ORDER IS BEHAVIOUR, NOT PRESENTATION. `nextStage()` reads this array, so
 * the Next button, `inboxStore.advance`, the funnel bar and every "Stage N of 6"
 * readout all follow it.
 *
 * ⭐ **Prioritize moved from 4th to directly after Clarify** (owner, 2026-08-06)
 * so that "what is this?" and "does it deserve attention?" are answered
 * together — which is the pair the Growth Module's step 1 now collects inline
 * before an item ever reaches this screen. An item arriving from that step is
 * already clarified and weighed, and picks the funnel up at `place`.
 *
 * ⚠️ NO MIGRATION, and none is possible to need: `stage` is stored per item as
 * a string id, so reordering rewrites nothing. What it does change is where an
 * OPEN item goes next — an item resting at `clarify` now advances to
 * `prioritize` rather than `place`. Nothing is lost either way; both stages
 * remain reachable with Back / Next.
 */
export const INBOX_STAGES: InboxStage[] = ['capture', 'clarify', 'prioritize', 'place', 'classify', 'route'];

export const STAGE_META: Record<InboxStage, {
  label: string;
  question: string;
  hint: string;
}> = {
  capture:    { label: 'Capture',    question: 'Just captured',                    hint: 'Not yet opened' },
  clarify:    { label: 'Clarify',    question: 'What is this, really?',            hint: 'Decide what it is' },
  prioritize: { label: 'Prioritize', question: 'Does it deserve attention now?',   hint: 'Weigh it up' },
  place:      { label: 'Place',      question: 'Where in your life does it fit?',  hint: 'Give it a domain' },
  // Splitting Classify from Route separates "what type should this become?" from
  // "am I actually committing to it?" — the old single Route step asked both at once
  // and gave no beat to review before the item left the funnel for good.
  classify:   { label: 'Classify',   question: 'What type of action is it?',       hint: 'Pick its form' },
  route:      { label: 'Route',      question: 'Ready to send it onward?',         hint: 'Review and commit' },
};

/**
 * ⚠️ NO stage auto-advances (owner call, 2026-07-27). Earlier versions moved the
 * item on as soon as a single-choice stage was answered, which meant a mis-tap
 * silently relocated the thought you were reading. Every stage is now stepped
 * explicitly with Back / Next, so the funnel never moves under you.
 */

/** The next stage in the funnel, or null at the exit. */
export function nextStage(stage: InboxStage): InboxStage | null {
  const i = INBOX_STAGES.indexOf(stage);
  return i >= 0 && i < INBOX_STAGES.length - 1 ? INBOX_STAGES[i + 1] : null;
}

/** The Clarify verdict — does anything need to happen? */
export type InboxOutcome =
  | 'actionable'
  | 'informational'
  | 'reflective'
  | 'deferred'
  | 'delegated'
  | 'archived';

/** Eisenhower quadrant, derived from important × urgent. */
export type EisenhowerQuadrant = 'do' | 'plan' | 'delegate' | 'eliminate';

export type Leverage = 'low' | 'medium' | 'high';

/** Layer 4 — can the user realistically act on this now? */
export type CapacityFit = 'now' | 'this-cycle' | 'next-cycle' | 'someday';

/**
 * Non-action destinations, alongside the buildable action types.
 *
 * `goal`, `skill` and `requirement` are NESTED types — they have no standalone
 * existence, so they are only reachable with a target (see ATTACH_SPEC).
 */
export type InboxDestination =
  | 'task' | 'event' | 'habit' | 'project' | 'program' | 'quest' | 'ability' | 'routine'
  | 'goal' | 'skill' | 'requirement'
  | 'journal' | 'reference' | 'someday' | 'none';

/**
 * ⭐ The THIRD axis: where a routed item LANDS.
 *
 * The inbox already separates kind (what it IS) from destination (what it
 * BECOMES); target answers WHERE. Modelling "add a task to a project", "a goal to
 * a quest", "a requirement to a skill" as extra destinations would have produced
 * an 18-chip grid in a 320px panel — the informational overwhelm this whole
 * feature exists to prevent — and would have implied things that don't exist:
 * there is no standalone Goal, so a bare "Goal" chip would be a lie.
 *
 * Instead: pick the type (8 chips), then pick where it attaches (one control,
 * scoped to types that can actually hold it).
 */
export type InboxTargetType = 'project' | 'taskFolder' | 'quest' | 'ability' | 'skill';

export interface InboxTarget {
  type: InboxTargetType;
  id: string;
  /** Denormalised so a card can render it without reaching into another store. */
  label: string;
  /** The owner, where the target is itself nested — a skill's ability. */
  parentId?: string;
  parentLabel?: string;
}

export const INBOX_TARGET_LABELS: Record<InboxTargetType, string> = {
  project: 'Project',
  taskFolder: 'Task folder',
  quest: 'Quest',
  ability: 'Ability',
  skill: 'Skill',
};

/**
 * Which containers each destination may attach to, and whether it MUST.
 *
 * `required: true` = the type is nested-only. Route stays blocked until a target
 * is chosen, because creating it standalone is not a thing the app can do.
 *
 * 🔌 SEAM — social request belongs here as
 *   `socialRequest: { targets: ['bond'], required: true }`
 * once its XP path has had a 2-account test. It is deliberately absent: it is the
 * only attach that writes to the SERVER (socialRequestsStore.createRequest)
 * rather than a local store, and routing one would mint XP down a path that has
 * never been exercised with two accounts.
 */
export const ATTACH_SPEC: Partial<Record<InboxDestination, {
  targets: InboxTargetType[];
  required: boolean;
}>> = {
  task:        { targets: ['project', 'taskFolder'], required: false },
  goal:        { targets: ['quest'],                 required: true  },
  skill:       { targets: ['ability'],               required: true  },
  requirement: { targets: ['skill'],                 required: true  },
};

const NO_ATTACH = { targets: [] as InboxTargetType[], required: false };

/** Attach rules for a destination — every destination has an answer. */
export function attachSpec(destination: InboxDestination) {
  return ATTACH_SPEC[destination] ?? NO_ATTACH;
}

/** True while a nested-only destination still has nowhere to go. */
export function needsTarget(destination: InboxDestination, target?: InboxTarget): boolean {
  return attachSpec(destination).required && !target;
}

/**
 * Destinations that create NOTHING outside the inbox.
 *
 * ⚠️ These must never end up `routed`. "Routed" means the thought became
 * something real elsewhere; filing a reference or deciding no action is needed
 * closes the loop INSIDE the inbox, and marking it routed would inflate the
 * "became something real" count with items that became nothing. Each maps to the
 * status it should actually take.
 *
 * `journal` is NOT here: it now creates a real dated journal entry (2026-07-27),
 * so `routed` is the truth for it. It was the one destination that promised an
 * entity and produced nothing.
 */
export const NON_ACTION_STATUS: Partial<Record<InboxDestination, 'parked' | 'archived'>> = {
  someday:   'parked',    // will come back
  reference: 'archived',  // kept for the record, closed
  none:      'archived',  // nothing to do, closed
};

/** Where a destination puts the item when committed. */
export function committedStatus(destination: InboxDestination): InboxStatus {
  return NON_ACTION_STATUS[destination] ?? 'routed';
}

/**
 * ⭐ The FOURTH axis: WHEN.
 *
 * Exactly the same shape of problem as the target axis, so it gets the same shape
 * of answer — ONE control INSIDE Classify, scoped to the destinations that can
 * actually carry a time. A seventh funnel stage was the alternative and was
 * rejected: it would be a no-op for ten of the fifteen destinations, and the
 * funnel already lost its bypass (auto-advance and Quick route are both gone), so
 * a stage most items skip would only train people to click through the ones that
 * matter. The date is also *about* the type just picked — "what form does this
 * take" and "when does that form happen" are one thought.
 *
 * ⚠️⚠️ This exists because three destinations were INVENTING the answer: a routed
 * Project silently got `startDate: today`, a Habit silently got `daily`, and an
 * Event silently got today's date. Same class of quiet lie as the journal
 * destination that promised an entry and produced nothing — the funnel earns its
 * trust by refusing to answer for the user, then used to answer three questions
 * for them on the way out.
 *
 * ⚠️ Not every "when" is a point in time. A habit's is a frequency and a
 * program's is a LENGTH — neither fits in a date input, and flattening them into
 * one would have made "Set Frequency" quietly mean "pick a day".
 */
export type InboxWhenKind = 'date' | 'span' | 'recurrence' | 'duration';

/**
 * What a program runs for when the user skips the question.
 * ⚠️ Exported so the fallback COPY and the routing DEFAULT are the same number —
 * the point of this axis is that the panel states what will be assumed, and two
 * hand-maintained 30s would eventually stop agreeing.
 */
export const DEFAULT_PROGRAM_DAYS = 30;

/** Common program lengths. Free entry stays available — real programs in the
 *  library run 21, 40, 56 and 63 days, so a preset list alone would be a lie. */
export const PROGRAM_DAY_PRESETS = [7, 21, 30, 60, 90];

export interface InboxWhenSpec {
  kind: InboxWhenKind;
  /** Section title in the picker — "When" for dates, "How often" for a habit. */
  title: string;
  /** Label for the primary date. */
  label: string;
  /** Label for the far end of a span. */
  endLabel?: string;
  /**
   * True = the entity genuinely cannot exist without it, so Route blocks.
   * ⚠️ Only Event. A required date on a field that is really optional is just a
   * seventh click, and the funnel has no clicks to spare.
   */
  required: boolean;
  /**
   * What gets assumed when this is left blank. SHOWN in the panel — a default is
   * fine, a *silent* default is the thing being fixed.
   */
  fallback?: string;
}

export const WHEN_SPEC: Partial<Record<InboxDestination, InboxWhenSpec>> = {
  event:   { kind: 'date',       title: 'When',      label: 'Date',      required: true },
  task:    { kind: 'date',       title: 'When',      label: 'Due date',  required: false, fallback: 'No due date' },
  goal:    { kind: 'date',       title: 'When',      label: 'Due date',  required: false, fallback: 'No deadline' },
  // ⭐ Someday is the reason this axis is worth more than five date pickers. It
  // parks the item today with nothing to bring it back — a black hole with a
  // friendly name. A revisit date is what makes parking honest.
  someday: { kind: 'date',       title: 'When',      label: 'Revisit on', required: false, fallback: 'Nothing will bring it back' },
  project: { kind: 'span',       title: 'When',      label: 'Starts', endLabel: 'Due', required: false, fallback: 'Starts today, no due date' },
  habit:   { kind: 'recurrence', title: 'How often', label: 'Frequency', required: false, fallback: 'Every day' },
  // A program has no start date of its own — it is dated by ENROLMENT, and its
  // own "when" is how long it runs for.
  program: { kind: 'duration',   title: 'How long',  label: 'Duration', required: false, fallback: `${DEFAULT_PROGRAM_DAYS} days` },
};

/** The "when" rules for a destination, or undefined when it carries no time. */
export function whenSpec(destination: InboxDestination): InboxWhenSpec | undefined {
  return WHEN_SPEC[destination];
}

/** True while a destination that CANNOT exist without a date still has none. */
export function needsWhen(destination: InboxDestination, item: Pick<InboxItem, 'scheduledFor'>): boolean {
  return Boolean(whenSpec(destination)?.required && !item.scheduledFor);
}

/**
 * True while a habit's frequency has no day picked (weekly / monthly / yearly) — it
 * could never be due, so it may not leave the funnel (utils/habitSchedule.ts).
 */
export function habitMissingDays(destination: InboxDestination, item: Pick<InboxItem, 'recurrence'>): boolean {
  return destination === 'habit' && scheduleMissingDays(item.recurrence);
}

/** Sunday-first, matching the app's calendar and `HabitSchedule.days`. */
export const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const INTERVAL_LABELS: Record<string, string> = {
  daily: 'Every day', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly',
};

/**
 * One human line for an item's "when", or undefined when it carries none.
 * Shared by the Route review and the card so the two cannot describe the same
 * decision differently.
 */
export function describeWhen(
  item: Pick<InboxItem, 'scheduledFor' | 'scheduledUntil' | 'recurrence' | 'durationDays'>,
  destination: InboxDestination
): string | undefined {
  const spec = whenSpec(destination);
  if (!spec) return undefined;

  if (spec.kind === 'recurrence') {
    const r = item.recurrence;
    if (!r) return undefined;
    return r.interval === 'weekly' && r.days?.length
      ? `Weekly on ${r.days.map(d => WEEKDAY_NAMES[d]).join(', ')}`
      : INTERVAL_LABELS[r.interval];
  }

  if (spec.kind === 'duration') {
    return item.durationDays ? `${item.durationDays} days` : undefined;
  }

  if (spec.kind === 'span') {
    if (!item.scheduledFor && !item.scheduledUntil) return undefined;
    const from = item.scheduledFor ?? 'today';
    return item.scheduledUntil ? `${from} → ${item.scheduledUntil}` : `Starts ${from}`;
  }

  return item.scheduledFor ? `${spec.label} ${item.scheduledFor}` : undefined;
}

/**
 * A parked item whose revisit date has arrived.
 * ⚠️ yyyy-MM-dd compares correctly as a string; do NOT parse it into a Date just
 * to compare, or a timezone shift turns "today" into yesterday.
 */
export function isDueToRevisit(
  item: Pick<InboxItem, 'status' | 'scheduledFor'>,
  today: string
): boolean {
  return item.status === 'parked' && !!item.scheduledFor && item.scheduledFor <= today;
}

/**
 * An attachment on an inbox item. Only the object PATH is stored — the bucket is
 * private, so playback mints a short-lived signed URL rather than holding a link
 * that would outlive the session.
 */
export interface InboxMedia {
  id: string;
  kind: 'voice' | 'image' | 'file';
  /** Object path in the private `inbox-media` bucket. */
  path: string;
  /** Seconds, for voice notes. */
  durationSec?: number;
  mimeType?: string;
  createdAt: string;
}

export interface InboxItem {
  id: string;
  title: string;
  /**
   * What this is about — the main body. Rich text (HTML from TipTap).
   * Was `note` before v4; migrated across.
   */
  description?: string;
  /** Working notes kept alongside the description. Rich text (HTML). */
  notes?: string;

  kind: InboxKind;
  /** Position in the funnel. Only meaningful while `status === 'open'`. */
  stage: InboxStage;
  /** Whether it's still in the funnel, parked, archived, or has exited. */
  status: InboxStatus;
  outcome?: InboxOutcome;

  // ── Categorize ────────────────────────────────────────────────────────────
  domain?: string;        // category/domain id
  /**
   * An area WITHIN the domain (a discipline id, or `general`).
   * ⚠️ Inbox-only today: Task/Habit/etc. carry no `areaId`, so this tag does NOT
   * follow the item into whatever it becomes. Making it follow needs an areaId on
   * the action types first — a separate change.
   */
  area?: string;
  questId?: string;       // connects to an existing quest
  projectId?: string;     // connects to an existing project

  // ── Prioritize ────────────────────────────────────────────────────────────
  important?: boolean;       // Eisenhower
  urgent?: boolean;          // Eisenhower
  leverage?: Leverage;       // effect relative to effort
  capacityFit?: CapacityFit; // can they act now?

  // ── Route ─────────────────────────────────────────────────────────────────
  destination?: InboxDestination;
  /** Where it attaches. Absent = standalone (and illegal for nested-only types). */
  target?: InboxTarget;
  routedEntityId?: string;

  // ── Classify: WHEN (see WHEN_SPEC) ────────────────────────────────────────
  /**
   * yyyy-MM-dd — the date the thing hangs off. Its MEANING is per destination
   * (event date · task/goal due date · project START · someday revisit), which is
   * exactly what WHEN_SPEC's labels disambiguate. One field rather than four
   * near-identical ones, because only one is ever in play at a time.
   */
  scheduledFor?: string;
  /** yyyy-MM-dd — the far end of a span (a project's due date). */
  scheduledUntil?: string;
  /**
   * How often, for recurring destinations (Habit).
   * ⚠️ A frequency is NOT a date. `scheduledFor` is a single string and cannot
   * hold `{interval, days}`, which is the whole reason this is its own field.
   */
  recurrence?: HabitSchedule;
  /**
   * How many days a Program runs for. ⚠️ Also not a date: a program has no start
   * of its own (enrolment dates it), so a date picker here would have asked the
   * wrong question entirely.
   */
  durationDays?: number;

  /** Voice notes and file attachments (private bucket paths, see migration 0236). */
  media?: InboxMedia[];

  createdAt: string;
  updatedAt: string;
  clarifiedAt?: string;
  routedAt?: string;
}

/** Human labels for each kind (UI + the clarify picker). */
export const INBOX_KIND_LABELS: Record<InboxKind, string> = {
  unclear: 'Not yet clear',
  action: 'Something to do',
  concern: 'A concern',
  idea: 'An idea',
  decision: 'A decision to make',
  commitment: 'A commitment',
  reminder: 'A reminder',
  reference: 'Reference / information',
  delegate: 'For someone else',
  none: 'No action needed',
};

/** The kinds offered in the Clarify step, in a sensible reading order. */
export const INBOX_KINDS: InboxKind[] = [
  'action', 'concern', 'idea', 'decision', 'commitment', 'reminder',
  'reference', 'delegate', 'none',
];

export const INBOX_DESTINATION_LABELS: Record<InboxDestination, string> = {
  goal: 'Goal',
  skill: 'Skill',
  requirement: 'Requirement',
  quest: 'Quest',
  ability: 'Ability',
  program: 'Program',
  project: 'Project',
  task: 'Task',
  event: 'Event',
  habit: 'Habit',
  routine: 'Routine',
  journal: 'Journal entry',
  reference: 'Keep as reference',
  someday: 'Someday / maybe',
  none: 'No action',
};

/** Destinations that create a real entity elsewhere in the app. */
export const ACTION_DESTINATIONS: InboxDestination[] = [
  'quest', 'ability', 'program', 'project', 'task', 'event', 'habit', 'routine',
  'goal', 'skill', 'requirement', 'journal',
];

/**
 * Classify's options, GROUPED — because these are three different kinds of
 * choice, not fifteen equivalent ones.
 *
 * A flat wrap of 15 chips read as an undifferentiated blob: nothing told you that
 * a Goal needs a container before it can exist, or that "Keep as reference"
 * produces nothing outside the inbox. The model already knew (ATTACH_SPEC,
 * NON_ACTION_STATUS); the picker just wasn't showing it. Grouping puts that
 * knowledge on screen, and a fixed grid replaces ragged wrapping.
 */
export const CLASSIFY_GROUPS: { title: string; hint: string; destinations: InboxDestination[] }[] = [
  {
    title: 'Becomes an action',
    hint: 'Creates a real item you can work on.',
    destinations: ['task', 'event', 'habit', 'routine', 'quest', 'project', 'program', 'ability', 'journal'],
  },
  {
    title: 'Goes inside something',
    hint: 'Has no standalone form — you pick where it lives next.',
    destinations: ['goal', 'skill', 'requirement'],
  },
  {
    title: 'Creates nothing',
    hint: 'Closes the loop without building anything.',
    destinations: ['reference', 'someday', 'none'],
  },
];

/** Flat reading order, derived from the groups so the two can't drift apart. */
export const CLASSIFY_DESTINATIONS: InboxDestination[] =
  CLASSIFY_GROUPS.flatMap(g => g.destinations);
